import { useEffect, useRef } from 'react';

/**
 * Runs an effect with debouncing so rapid dependency changes collapse into a single call. Invokes the function immediately on the first pass and arms a timer that re-enables immediate mode, and on later changes clears the pending timer and reschedules the call after the delay.
 *
 * @param fnc - Function to invoke, immediately or after the delay.
 * @param deps - Dependencies that retrigger the debounced call.
 * @param delay - Debounce window in milliseconds.
 * @returns Nothing.
 */
const useDebounceEffect = (fnc: (...args: any[]) => any, deps: any[] = [], delay = 500) => {
  const ref = useRef<any>(undefined);
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      fnc();
      isFirst.current = false;
      ref.current = setTimeout(() => {
        isFirst.current = true;
      }, delay);
      return;
    }
    clearTimeout(ref.current);
    ref.current = setTimeout(() => {
      fnc();
      isFirst.current = true;
    }, delay);
  }, [...deps, delay]);
};

export default useDebounceEffect;
